import { useEffect } from 'react';

const shouldOpen = (href: string, target: EventTarget | null) => {
  // document.activeElement points to the element that currently has focus,
  // usually it is the most recent thing that was clicked (and can be focused).
  // This is what we actually need when clicking anything wrapped in an Anchor -
  // - image or other passive elements cannot have focus so it goes to the wrapping anchor.
  if (document.activeElement) {
    return document.activeElement instanceof HTMLAnchorElement && document.activeElement.hash === href;
  }

  // fallback code for the 0.1% cases where document does not provide activeElement -
  // - pick the event target and if it is not an Anchor, see if any of its parents is.
  // If we don't find one, stop at Body - there's nothing of interest upwards.
  let bubblingTarget = target as HTMLElement | null;
  while (bubblingTarget && !(bubblingTarget instanceof HTMLBodyElement)) {
    if (bubblingTarget instanceof HTMLAnchorElement) {
      return bubblingTarget.hash === href;
    }
    bubblingTarget = bubblingTarget.parentElement;
  }

  return false;
};

export interface UseOnHrefClickProps {
  href: string;
  onClick: () => void;
}

export const useOnHrefClick = ({ href, onClick }: UseOnHrefClickProps) =>
  useEffect(() => {
    const onOpen = (event: MouseEvent) => {
      if (shouldOpen(href, event.target)) {
        event.preventDefault();
        onClick();
      }
    };
    document.addEventListener('click', onOpen);

    return () => document.removeEventListener('click', onOpen);
  }, [href, onClick]);
